import React from 'react';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import PageHero from '../../components/PageHero/PageHero';
import Footer from '../../components/Footer/Footer';

const ContentContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
}));

const ErrorCode = styled(Typography)(({ theme }) => ({
  fontSize: '8rem',
  fontWeight: 900,
  color: '#85b47c',
  lineHeight: 1,
  marginBottom: theme.spacing(2),
  textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '6rem',
  },
}));

const ErrorTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#2a3a28',
  marginBottom: theme.spacing(2),
  fontFamily: '"Lora", serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const ErrorDescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#666666',
  marginBottom: theme.spacing(4),
  maxWidth: '600px',
  margin: '0 auto',
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#85b47c',
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '12px 28px',
  borderRadius: '12px',
  border: `2px solid #85b47c`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#6a8f61',
    borderColor: '#6a8f61',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 24px',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#455b40',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: '"Lora", serif',
  padding: '12px 28px',
  borderRadius: '12px',
  border: `2px solid #455b40`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#455b40',
    color: '#FFFFFF',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '10px 24px',
  },
}));

const SuggestionsList = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  padding: theme.spacing(4),
  backgroundColor: '#f5f5f5',
  borderRadius: '16px',
  maxWidth: '800px',
  margin: '48px auto 0',
}));

const SuggestionItem = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666666',
  marginBottom: theme.spacing(1),
  textAlign: 'left',
  '& a': {
    color: '#85b47c',
    textDecoration: 'none',
    fontWeight: 600,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const NotFound: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <PageHero
        title="Página no encontrada"
        subtitle="Oops! Parece que te has perdido"
        backgroundImage="/Finca_ecológica_Valencia.jpg"
        backgroundColor="#f5f5f5"
        showBreadcrumbs={true}
        breadcrumbs={[
          { label: 'Inicio', link: '/' },
          { label: 'Error 404', link: '#' },
        ]}
      />

      <ContentContainer maxWidth="lg">
        <ErrorCode variant="h1">404</ErrorCode>
        
        <ErrorTitle variant="h2">
          ¡Ups! Esta página no existe
        </ErrorTitle>
        
        <ErrorDescription variant="body1">
          Lo sentimos, pero la página que estás buscando no se encuentra disponible. 
          Puede que haya sido movida, eliminada o que la dirección no sea correcta.
        </ErrorDescription>

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          sx={{ mt: 4, mb: 6 }}
        >
          <ActionButton
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
          >
            Volver al Inicio
          </ActionButton>
          
          <SecondaryButton
            startIcon={<SearchIcon />}
            onClick={() => navigate('/cursos')}
          >
            Explorar Cursos
          </SecondaryButton>
        </Stack>

        <SuggestionsList>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: '#2a3a28',
              marginBottom: 2,
              fontFamily: '"Lora", serif',
            }}
          >
            Enlaces útiles que podrían interesarte:
          </Typography>
          
          <Stack spacing={1}>
            <SuggestionItem>
              • Descubre <a href="/quienes-somos">quiénes somos</a> y nuestra misión
            </SuggestionItem>
            <SuggestionItem>
              • Explora nuestros <a href="/cursos">cursos de agricultura ecológica</a>
            </SuggestionItem>
            <SuggestionItem>
              • Conoce <a href="/la-finca">nuestra finca ecológica</a> en Bétera
            </SuggestionItem>
            <SuggestionItem>
              • Lee las últimas <a href="/noticias-blog">noticias y artículos del blog</a>
            </SuggestionItem>
            <SuggestionItem>
              • <a href="/contacto">Contáctanos</a> si necesitas ayuda
            </SuggestionItem>
          </Stack>
        </SuggestionsList>
      </ContentContainer>

      <Footer />
    </>
  );
};

export default NotFound;